import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  CurrencyEnum,
  type CurrencyCode,
} from "@/features/events/proposal/add-services/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Gets initials from a name (first letter of each word, up to 2 characters)
 * @param name - The name to get initials from
 * @returns Up to 2 uppercase initials (e.g., "John Doe" -> "JD", "Mary Jane Watson" -> "MJ")
 */
export function getInitials(name: string): string {
  if (!name || typeof name !== "string") {
    return "??";
  }

  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .substring(0, 2)
    .toUpperCase();
}

/**
 * Format currency using Intl.NumberFormat for proper localization
 * @param amount - The amount to format
 * @param currency - The currency code (default: EUR)
 * @param locale - The locale to use for formatting (default: en-EU)
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency: CurrencyCode = CurrencyEnum.EUR,
  locale = "en-EU",
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format file size in bytes to human readable format
 * @param bytes - The file size in bytes
 * @returns Formatted file size string (e.g., "1.2 MB", "500 KB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

/**
 * Checks if an error is a Next.js redirect error
 */
export function isNextRedirect(error: unknown): boolean {
  return !!(
    error &&
    typeof error === "object" &&
    "digest" in error &&
    typeof (error as { digest: unknown }).digest === "string" &&
    (error as { digest: string }).digest.includes("NEXT_REDIRECT")
  );
}

/**
 * Handles errors by re-throwing Next.js redirects and returning a fallback for other errors
 * @param error - The error to handle
 * @param fallback - The fallback value to return for non-redirect errors
 * @param context - Optional context string for logging
 * @param shouldRethrowErrors - Whether to re-throw non-redirect errors (default: false)
 * @returns The fallback value, or throws if it's a redirect or shouldRethrowErrors is true
 */
export function handleErrorWithRedirect<T>(
  error: unknown,
  fallback: T,
  context?: string,
  shouldRethrowErrors = false,
): T | never {
  if (isNextRedirect(error)) {
    console.log(
      `🔄 ${context ? `[${context}] ` : ""}Next.js redirect detected, re-throwing`,
    );
    throw error;
  }

  // Log the error
  console.error(`${context ? `Error in ${context}: ` : "Error: "}`, error);

  // Decide whether to re-throw or return fallback
  if (shouldRethrowErrors) {
    throw error;
  }

  return fallback;
}

/**
 * Cleans HTML content by removing problematic style attributes
 * This function removes [object Object] from style attributes, empty style attributes,
 * and style attributes that only contain whitespace
 */
export function cleanHtml(html: string): string {
  if (!html || typeof html !== "string") {
    return "";
  }

  let cleanedHtml = html;

  // Remove [object Object] from style attributes
  cleanedHtml = cleanedHtml.replace(/style="[^"]*\[object Object\][^"]*"/g, "");

  // Remove empty style attributes
  cleanedHtml = cleanedHtml.replace(/style=""/g, "");

  // Remove style attributes that only contain whitespace
  cleanedHtml = cleanedHtml.replace(/style="\s*"/g, "");

  return cleanedHtml;
}

/**
 * Strips HTML tags and decodes HTML entities from a string
 * This function removes all HTML tags and converts HTML entities to plain text
 * Useful for displaying HTML content as plain text
 */
export function stripHtml(html: string): string {
  if (!html || typeof html !== "string") {
    return "";
  }

  // Remove HTML tags
  let text = html.replace(/<[^>]*>/g, "");

  // Decode common HTML entities
  text = text
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ");

  // Clean up extra whitespace
  text = text.replace(/\s+/g, " ").trim();

  return text;
}

/**
 * Type for translation functions that accept a key and optional values
 */
export type TranslationFunction = (
  key: string,
  values?: Record<string, unknown>,
) => string;

/**
 * Formats a duration in hours to a human-readable string
 * @param hours - Duration in hours (can be fractional)
 * @param t - Translation function for localization
 * @returns Formatted duration string (e.g., "2h 30m", "45m", "3h")
 */
export function formatDurationHours(
  hours: number,
  t?: TranslationFunction,
): string {
  if (hours <= 0) {
    return t ? t("durationFormat.zero") : "0h 0m";
  }

  const totalMinutes = Math.floor(hours * 60);
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (t) {
    // Use translations when available
    if (wholeHours === 0) {
      return t("durationFormat.minutes", { minutes });
    } else if (minutes === 0) {
      return t("durationFormat.hours", { hours: wholeHours });
    } else {
      return t("durationFormat.hoursAndMinutes", {
        hours: wholeHours,
        minutes,
      });
    }
  } else {
    // Fallback to English when no translation function provided
    if (wholeHours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${wholeHours}h`;
    } else {
      return `${wholeHours}h ${minutes}m`;
    }
  }
}
